import type { Action, Agent } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import { TriggerOption } from '@/components/flow';
import { toAgentId, useCreateAction, useUpdateAgent } from '@/hooks/api';

export type UseAddTriggerOnSuccess = ({ action, agent }: { action: Action; agent: Agent }) => void;
export type UseAddTriggerOnError = ({ error }: { error: Error }) => void;
export type UseAddTriggerCallbackArgs = {
  agentId: string;
  triggerOption: TriggerOption;
  onSuccess?: UseAddTriggerOnSuccess;
  onError?: UseAddTriggerOnError;
};
export type UseAddTriggerCallback = ({ agentId, triggerOption, onSuccess, onError }: UseAddTriggerCallbackArgs) => void;

export type UseAddTriggerOpts = {
  onSuccess?: UseAddTriggerOnSuccess;
  onError?: UseAddTriggerOnError;
};

export const useAddTrigger = ({ onSuccess: onSuccessFnBase, onError: onErrorFnBase }: UseAddTriggerOpts = {}) => {
  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();
  const {
    mutateAsync: createAction,
    isPending: isPendingCreateAction,
    isSuccess: isSuccessCreateAction,
    error: errorCreateAction,
  } = useCreateAction();

  const addTrigger: UseAddTriggerCallback = useCallback(
    async ({ agentId, triggerOption, onSuccess: onSuccessFn, onError: onErrorFn }: UseAddTriggerCallbackArgs) => {
      const onSuccess: UseAddTriggerOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };
      const onError: UseAddTriggerOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      try {
        const action = await createAction({
          config: typeof triggerOption.config === 'function' ? triggerOption.config() : triggerOption.config,
          functionId: triggerOption.functionId,
          name: triggerOption.name,
        });

        const agent = await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [...agent.resourceIds, action.actionId],
          }),
        });

        onSuccess?.({ action, agent });

        return action;
      } catch (error) {
        if (error instanceof Error) {
          onError?.({ error });
        }
      }
    },
    [createAction, onErrorFnBase, onSuccessFnBase, updateAgent]
  );

  return {
    addTrigger,
    isPending: isPendingCreateAction || isPendingUpdateAgent,
    isSuccess: isSuccessCreateAction && isSuccessUpdateAgent,
    errors: [errorCreateAction, errorUpdateAgent].filter((e) => !!e),
  };
};
